import React, { useState, useEffect } from "react";
import { Questionnaire, QuestionnaireDefinition } from "../common/models";
import { QuestionnaireTaskView } from "../common/QuestionnaireTaskView"
import Typography from "@mui/material/Typography";
import { useStorage } from "../../../../services/StorageProvider";

const Definition: QuestionnaireDefinition = {
  name: 'Confounders',
  key: 'confounders',
  title: 'Tell us about your health',
  description:'',
  type: 'questionnaire',
  estimatedCompletionTime: '10:35',
  cohort: 'generic',
  groups: [
    {
      key: 'medicalHistoryEarsNoseMouth',
      title: 'Medical History - Ears, Nose, Mouth conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            There are some medical conditions that could have long term effects on your voice. In the following questions you will be asked about your CURRENT conditions as well as those you may have had in the PAST. Please read each set of questions carefully.
          </Typography>
          <Typography variant="body1" style={{ marginTop: '10px' }}>
            Have you ever been diagnosed by a doctor with any of the following conditions that affect your EARS, NOSE, or MOUTH?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'chronicEarInfections',
          type: 'multiple-choice',
          question: 'Chronic ear infections',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'cleftLip',
          type: 'multiple-choice',
          question: 'Cleft lip',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'cleftPalate',
          type: 'multiple-choice',
          question: 'Cleft palate',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'craniofacialTrauma',
          type: 'multiple-choice',
          question: 'Craniofacial trauma',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'hearingLoss',
          type: 'multiple-choice',
          question: 'Hearing loss',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'seasonalAllergies',
          type: 'multiple-choice',
          question: 'Seasonal allergies',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'sinusitis',
          type: 'multiple-choice',
          question: 'Sinusitis (rhinitis)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'sjogrensSyndrome',
          type: 'multiple-choice',
          question: `Sjogren's syndrome`,
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'velopharyngealInsufficiency',
          type: 'multiple-choice',
          question: 'Velopharyngeal insufficiency (e.g. hyponasality, nasal emission, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'anyDentalCondition',
          type: 'multiple-choice',
          question: 'Do you currently have any dental condition(s) (e.g. tooth loss, dentures, retainer, braces, etc.)?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
        {
          key: 'anyDentalConditionSpecified',
          type: 'text',
          question: 'Please specify what dental condition(s) you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouth');
            const question = group?.questions.find(x => x.key === 'anyDentalCondition');
            return question?.value === 'yes';
          }
        },
        {
          key: 'entConditionOther',
          type: 'multiple-choice',
          question: 'Do you currently have any other condition(s) of your ear, nose, or mouth?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'entConditionOtherSpecified',
          type: 'text',
          question: 'Please specify what other condition(s) of your ear, nose, or mouth you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouth');
            const question = group?.questions.find(x => x.key === 'entConditionOther');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'medicalHistoryThroat',
      title: 'Medical History - Throat conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of the following conditions that affect your THROAT? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'acidRefluxSelection',
          type: 'multiple-choice-multi-select',
          question: 'Acid reflux (e.g. heart burn, GERD, etc.)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'airwayStenosisSelection',
          type: 'multiple-choice-multi-select',
          question: 'Airway stenosis (e.g. glottal, subglottal, tracheal, etc.)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'chronicCoughSelection',
          type: 'multiple-choice-multi-select',
          question: 'Chronic cough',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'glotticInsufficiencySelection',
          type: 'multiple-choice-multi-select',
          question: 'Glottic insufficiency, presbyphonia',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'laryngitisSelection',
          type: 'multiple-choice-multi-select',
          question: 'Laryngitis',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'laryngospasmSelection',
          type: 'multiple-choice-multi-select',
          question: 'Laryngospasm, irritable larynx',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'muscleTensionDysphoniaSelection',
          type: 'multiple-choice-multi-select',
          question: 'Muscle tension dysphonia',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'swallowingDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Swallowing disorder/dysphagia',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'thyroidDiseaseSelection',
          type: 'multiple-choice-multi-select',
          question: `Thyroid disease (e.g. thyroid polyp, Hashimoto's Grave's disease, hypo/hyperthyroidism, etc.)`,
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'vocalCordDysfunctionSelection',
          type: 'multiple-choice-multi-select',
          question: 'Vocal cord dysfunction/paradoxical vocal fold motion',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'vocalFoldHemorrhageSelection',
          type: 'multiple-choice-multi-select',
          question: 'Vocal fold hemorrhage or bleed',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'lesionsVocalCordSelection',
          type: 'multiple-choice-multi-select',
          question: 'Lesions of the vocal cord',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'vfpBilateralSelection',
          type: 'multiple-choice-multi-select',
          question: 'Vocal fold paralysis (bilateral)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'vfpUnilateralSelection',
          type: 'multiple-choice-multi-select',
          question: 'Vocal fold paralysis (unilateral)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'throatConditionOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other throat condition',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'throatConditionOtherCurrentSpecified',
          type: 'text',
          question: 'Please specify what other throat condition(s) you currently have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryThroat');
            const question = group?.questions.find(x => x.key === 'throatConditionOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        },
        {
          key: 'dailyEnvPollutionAffectsYourBreathingVoice',
          type: 'multiple-choice',
          question: 'Do you feel as though everyday environmental pollution (e.g. smog, smoke, dust, etc) significantly affects your breathing or voice?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'notCertain', displayName: ' Not Certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
      ]
    },
    {
      key: 'medicalHistorySpeech',
      title: 'Medical History - Speech conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of these other SPEECH conditions?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'apraxiaOfSpeech',
          type: 'multiple-choice',
          question: 'Apraxia of speech',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'ataxia',
          type: 'multiple-choice',
          question: 'Ataxia',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'dysarthria',
          type: 'multiple-choice',
          question: 'Dysarthria',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'stutterDysfluency',
          type: 'multiple-choice',
          question: 'Stutter/dysfluency',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'speechConditionOther',
          type: 'multiple-choice',
          question: 'Other speech condition',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'speechConditionOtherSpecified',
          type: 'text',
          question: 'Please specify what other speech condition(s) you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistorySpeech');
            const question = group?.questions.find(x => x.key === 'speechConditionOther');
            return question?.value === 'yes';
          }
        }
      ]
    },
    {
      key: 'medicalHistoryEarsNoseMouthThroatSpeechSurgicalInterventions',
      title: 'Medical History - Ears, Nose, Mouth, Throat, or Speech surgical interventions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following surgical interventions for your EAR, NOSE, MOUTH, THROAT, or SPEECH condition(s)?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryEarsNoseMouthGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouth');
        const medicalHistoryThroatGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryThroat');
        const medicalHistorySpeechGroup = questionnaire.groups.find(x => x.key === 'medicalHistorySpeech');
        const hasYesInEarsNoseMouthSelected = medicalHistoryEarsNoseMouthGroup?.questions.some(q => q.value === 'yes') || false;
        const hasPastOrCurrentSelected = medicalHistoryThroatGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
        const hasYesInSpeechSelected = medicalHistorySpeechGroup?.questions.some(q => q.value === 'yes') || false;
        return hasYesInEarsNoseMouthSelected || hasPastOrCurrentSelected || hasYesInSpeechSelected;
      },
      questions: [
        {
          key: 'airwaySurgeryReconstruction',
          type: 'multiple-choice',
          question: 'Airway surgery/reconstruction BELOW the vocal folds (e.g. tracheal reconstruction, balloon dilation, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'chronicEarSurgery',
          type: 'multiple-choice',
          question: 'Chronic ear surgery (e.g. mastoid surgery, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'cochlearImplant',
          type: 'multiple-choice',
          question: 'Cochlear implant',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'cordotomyArytenoidectomyArytenoidTieBack',
          type: 'multiple-choice',
          question: 'Cordotomy, arytenoidectomy, arytenoid tie back, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'earTubes',
          type: 'multiple-choice',
          question: 'Ear tubes',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'esophagealDilation',
          type: 'multiple-choice',
          question: 'Esophageal dilation',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'noseSurgery',
          type: 'multiple-choice',
          question: 'Nose surgery (e.g. septoplasty, rhinoplasty, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'sinusSurgery',
          type: 'multiple-choice',
          question: 'Sinus surgery',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'sleepSurgery',
          type: 'multiple-choice',
          question: 'Sleep surgery (e.g. implantable device for sleep apnea, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'tonsillectomy',
          type: 'multiple-choice',
          question: 'Tonsillectomy (with or without adenoidectomy)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'tracheostomy',
          type: 'multiple-choice',
          question: 'Tracheostomy',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'enmtSpeechConditionSurgicalTreatmentOther',
          type: 'multiple-choice',
          question: 'Other surgical treatment for an ear, nose, mouth, throat, or speech condition',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'enmtSpeechConditionSurgicalTreatmentOtherSpecified',
          type: 'text',
          question: 'Please specify what other surgical treatment(s) you have received for your ear, nose, mouth, throat, or speech condition(s).',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouthThroatSpeechSurgicalInterventions');
            const question = group?.questions.find(x => x.key === 'enmtSpeechConditionSurgicalTreatmentOther');
            return question?.value === 'yes';
          }
        }
      ]
    },
    {
      key: 'medicalHistoryEarsNoseMouthThroatSpeechNonSurgicalInterventions',
      title: 'Medical History - Ears, Nose, Mouth, Throat, or Speech non-surgical interventions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following non-surgical interventions for your EAR, NOSE, MOUTH, THROAT, or SPEECH condition(s)? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the intervention, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryEarsNoseMouthGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouth');
        const medicalHistoryThroatGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryThroat');
        const medicalHistorySpeechGroup = questionnaire.groups.find(x => x.key === 'medicalHistorySpeech');
        const hasYesInEarsNoseMouthSelected = medicalHistoryEarsNoseMouthGroup?.questions.some(q => q.value === 'yes') || false;
        const hasPastOrCurrentSelected = medicalHistoryThroatGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
        const hasYesInSpeechSelected = medicalHistorySpeechGroup?.questions.some(q => q.value === 'yes') || false;
        return hasYesInEarsNoseMouthSelected || hasPastOrCurrentSelected || hasYesInSpeechSelected;
      },
      questions: [
        {
          key: 'botoxInjectionsSelection',
          type: 'multiple-choice-multi-select',
          question: 'Botox injections for laryngeal dystonia',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'cPapSelection',
          type: 'multiple-choice-multi-select',
          question: 'C-Pap',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'injectionImplantToVocalFoldsSelection',
          type: 'multiple-choice-multi-select',
          question: 'Injection/implant to the vocal folds',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'medicationEntmSelection',
          type: 'multiple-choice-multi-select',
          question: 'Medication',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'radiationToHeadNeckSelection',
          type: 'multiple-choice-multi-select',
          question: 'Radiation to the head or neck',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'speechVoiceSwallowingTherapySelection',
          type: 'multiple-choice-multi-select',
          question: 'Speech, voice, or swallowing therapy',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'enmtSpeechConditionNonSurgicalTreatmentOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other non-surgical treatment for an ear, nose, mouth, throat, or speech condition',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'enmtSpeechConditionNonSurgicalTreatmentOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other non-surgical treatment(s) you are currently receiving for your ear, nose, mouth, throat, or speech condition(s).',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouthThroatSpeechNonSurgicalInterventions');
            const question = group?.questions.find(x => x.key === 'enmtSpeechConditionNonSurgicalTreatmentOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        },
      ]
    },
    {
      key: 'medicalHistoryLungConditions',
      title: 'Medical History - Lung conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of the following LUNG conditions?

          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'asthma',
          type: 'multiple-choice',
          question: 'Asthma',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'bronchiectasis',
          type: 'multiple-choice',
          question: 'Bronchiectasis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'chestWallTrauma',
          type: 'multiple-choice',
          question: 'Chest wall trauma',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'copd',
          type: 'multiple-choice',
          question: 'Chronic obstructive pulmonary disease (COPD)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'covid',
          type: 'multiple-choice',
          question: 'COVID',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'covidTestedPositiveLast10Days',
          type: 'multiple-choice',
          question: 'Have you tested positive for COVID in the last 10 days?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryLungConditions');
            const question = group?.questions.find(x => x.key === 'covid');
            return question?.value === 'yes';
          }
        },
        {
          key: 'emphysema',
          type: 'multiple-choice',
          question: 'Emphysema',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'interstitial',
          type: 'multiple-choice',
          question: 'Interstitial lung disease (e.g. sarcoidosis, pulmonary fibrosis)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'osa',
          type: 'multiple-choice',
          question: 'Obstructive sleep apnea (OSA)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'pneumothoraxAtelectasis',
          type: 'multiple-choice',
          question: 'Pneumothorax or atelectasis (e.g. collapsed lung)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'pulmonaryHypertension',
          type: 'multiple-choice',
          question: 'Pulmonary hypertension',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'recurrentBronchitis',
          type: 'multiple-choice',
          question: 'Recurrent bronchitis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'tuberculosis',
          type: 'multiple-choice',
          question: 'Tuberculosis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'lungConditionOther',
          type: 'multiple-choice',
          question: 'Do you currently have any other lung condition?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
        {
          key: 'lungConditionOtherSpecified',
          type: 'text',
          question: 'Please specify what other lung condition(s) you currently have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryLungConditions');
            const question = group?.questions.find(x => x.key === 'lungConditionOther');
            return question?.value === 'yes';
          }
        }
      ]
    },
    {
      key: 'medicalHistoryLungTreatments',
      title: 'Medical History - Lung treatments',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following interventions for your LUNG condition(s)?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “No”. Please only change the responses that apply to you. If you have never had the intervention, you can leave the response as it is.

          </Typography>
        </React.Fragment>
      ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryLungConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryLungConditions');
        const hasYesInLungConditionsSelected = medicalHistoryLungConditionsGroup?.questions.some(q => q.value === 'yes') || false;
        return hasYesInLungConditionsSelected;
      },
      questions: [
        {
          key: 'chestSurgery',
          type: 'multiple-choice',
          question: 'Chest surgery (e.g. bronchoscopy, thoracic surgery, etc.)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'lobectomy',
          type: 'multiple-choice',
          question: 'Lobectomy',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationLung',
          type: 'multiple-choice',
          question: 'Medication',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'prolongedIntubation',
          type: 'multiple-choice',
          question: 'Prolonged intubation (more than 7 days)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'radiationToTheChest',
          type: 'multiple-choice',
          question: 'Radiation to the chest',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'supplementalOxygen',
          type: 'multiple-choice',
          question: 'Supplemental oxygen',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'lungTreatmentOther',
          type: 'multiple-choice',
          question: 'Are you currently receiving any other treatment(s) for your lung condition(s)?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
        {
          key: 'lungTreatmentOtherSpecified',
          type: 'text',
          question: 'Please specify what other treatment(s) you are receiving for your lung condition(s).',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryLungTreatments');
            const question = group?.questions.find(x => x.key === 'lungTreatmentOther');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'medicalHistoryNeurologicalConditions',
      title: 'Medical History - Neurological conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of the following NEUROLOGICAL conditions?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'brainTumor',
          type: 'multiple-choice',
          question: 'Brain tumor',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'epilepsy',
          type: 'multiple-choice',
          question: 'Epilepsy',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'multipleSclerosis',
          type: 'multiple-choice',
          question: 'Multiple sclerosis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'parkinsonsDisease',
          type: 'multiple-choice',
          question: `Parkinson's Disease`,
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'stroke',
          type: 'multiple-choice',
          question: 'Stroke/aphasia',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'traumaticBrainInjury',
          type: 'multiple-choice',
          question: 'Traumatic brain injury',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'neurologicalConditionOther',
          type: 'multiple-choice',
          question: 'Do you currently have any other neurological condition(s)?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
        {
          key: 'neurologicalConditionOtherSpecified',
          type: 'text',
          question: 'Please specify what other neurological condition(s) you currently have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryNeurologicalConditions');
            const question = group?.questions.find(x => x.key === 'neurologicalConditionOther');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'medicalHistoryNeurologicalTreatments',
      title: 'Medical History - Neurological treatments',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following interventions for your NEUROLOGICAL condition(s)? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the intervention, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryNeurologicalConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryNeurologicalConditions');
        const hasYesInNeurologicalConditionsSelected = medicalHistoryNeurologicalConditionsGroup?.questions.some(q => q.value === 'yes') || false;
        return hasYesInNeurologicalConditionsSelected;
      },
      questions: [
        {
          key: 'deepBrainStimulatorSelection',
          type: 'multiple-choice-multi-select',
          question: 'Deep brain stimulator',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'medicationNeurologicalSelection',
          type: 'multiple-choice-multi-select',
          question: 'Medication',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'vagalNerveStimulatorSelection',
          type: 'multiple-choice-multi-select',
          question: 'Vagal nerve stimulator',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'neurologicalTreatmentOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other treatment for a neurological condition',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'neurologicalTreatmentOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other treatment(s) you are currently receiving for your neurological condition(s).',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryNeurologicalTreatments');
            const question = group?.questions.find(x => x.key === 'neurologicalTreatmentOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        }
      ]
    },
    {
      key: 'medicalHistoryPsychologicalConditions',
      title: 'Medical History - Psychological conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of the following PSYCHOLOGICAL conditions? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'alcoholSubstanceUseDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Alcohol or substance use disorder',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'anxietyDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Anxiety disorder (e.g. generalized or social anxiety disorder)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'adhdSelection',
          type: 'multiple-choice-multi-select',
          question: 'Attention deficit/hyperactivity disorder (ADHD)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'asdSelection',
          type: 'multiple-choice-multi-select',
          question: 'Autism spectrum disorder (ASD)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'bipolarDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Bipolar disorder',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'bpdSelection',
          type: 'multiple-choice-multi-select',
          question: 'Borderline personality disorder (BPD)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'depressionMajorDepressiveDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Depression or major depressive disorder',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'eatingDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Eating disorder (ED)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'obsessiveCompulsiveDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Obsessive compulsive disorder (OCD)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
                {
          key: 'panicDisorderSelection',
          type: 'multiple-choice-multi-select',
          question: 'Panic disorder',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'ptsdSelection',
          type: 'multiple-choice-multi-select',
          question: 'Post-traumatic stress disorder (PTSD)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'schizophreniaSelection',
          type: 'multiple-choice-multi-select',
          question: 'Schizophrenia',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'psychologicalConditionOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other psychological condition',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'psychologicalConditionOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other psychological condition(s) you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryPsychologicalConditions');
            const question = group?.questions.find(x => x.key === 'psychologicalConditionOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        }
      ]
    },
    {
      key: 'medicalHistoryPsychologicalTreatments',
      title: 'Medical History - Psychological treatments',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following interventions for your PSYCHOLOGICAL condition(s)? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryPsychologicalConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryPsychologicalConditions');
        const hasPastOrCurrentSelected = medicalHistoryPsychologicalConditionsGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
        return hasPastOrCurrentSelected;
      },
      questions: [
        {
          key: 'talkTherapyWithMentalHealthProfessionalSelection',
          type: 'multiple-choice-multi-select',
          question: 'Talk therapy with a mental health professional (e.g. social worker, psychiatrist, etc.)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'medicationPsychologicalSelection',
          type: 'multiple-choice-multi-select',
          question: 'Medication',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'mindfulnessMeditationSelection',
          type: 'multiple-choice-multi-select',
          question: 'Mindfulness, meditation',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'psychologicalConditionTreatmentOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other treatment for a psychological condition',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'psychologicalConditionTreatmentOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other treatment(s) you are currently receiving for your psychological condition(s).',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryPsychologicalTreatments');
            const question = group?.questions.find(x => x.key === 'psychologicalConditionTreatmentOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;

          }
        }
      ]
    },
    {
      key: 'medicalHistoryCancersConditions',
      title: 'Medical History - Cancer conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of the following CANCERS? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “Never”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'brainCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Brain cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'earCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Ear cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'laryngealCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Laryngeal cancer (e.g. vocal folds, epiglottis, etc.)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'lungCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Lung cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'oralCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Oral cancer (e.g. tongue, mouth, gum, tonsil, etc.)',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'sinusCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Sinus cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'esophagealCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Esophageal cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'thyroidCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Thyroid cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'cancerOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other cancer',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: ['never'],
          defaultValue: [],
          required: true
        },
        {
          key: 'cancerOtherSelectionPastSpecified',
          type: 'text',
          question: 'Please specify what other cancer(s) you have had in the past.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersConditions');
            const question = group?.questions.find(x => x.key === 'cancerOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('past')) ?? false;
          }
        },
        {
          key: 'cancerOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other cancer(s) you have currently.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersConditions');
            const question = group?.questions.find(x => x.key === 'cancerOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        }
      ]
    },
    {
      key: 'medicalHistoryCancersTreatments',
      title: 'Medical History - Cancer treatments',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever had any of the following interventions for your CANCER(S)? Please check all that apply.
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NEVER”. Please only change the responses that apply to you. If you have never had the intervention, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryCancerConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersConditions');
        const hasPastOrCurrentSelected = medicalHistoryCancerConditionsGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
        return hasPastOrCurrentSelected;
      },
      questions: [
        {
          key: 'chemotherapySelection',
          type: 'multiple-choice-multi-select',
          question: 'Chemotherapy',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'surgerySelection',
          type: 'multiple-choice-multi-select',
          question: 'Surgery',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'immunotherapySelection',
          type: 'multiple-choice-multi-select',
          question: 'Immunotherapy',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'radiationSelection',
          type: 'multiple-choice-multi-select',
          question: 'Radiation',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'radioactiveIodineSelection',
          type: 'multiple-choice-multi-select',
          question: 'Radioactive Iodine',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'medicationCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Medication',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'experimentalTreatmentCancerSelection',
          type: 'multiple-choice-multi-select',
          question: 'Experimental treatment / clinical trial',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'experimentalTreatmentCancerSelectionPastSpecified',
          type: 'text',
          question: 'Please specify what experimental treatment (e.g. clinical trial) you received in the past.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersTreatments');
            const question = group?.questions.find(x => x.key === 'experimentalTreatmentCancerSelection');
            return (Array.isArray(question?.value) && question.value.includes('past')) ?? false;
          }
        },
        {
          key: 'experimentalTreatmentCancerSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what experimental treatment (e.g. clinical trial) you are currently receiving.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersTreatments');
            const question = group?.questions.find(x => x.key === 'experimentalTreatmentCancerSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        },
        {
          key: 'cancerTreatmentOtherSelection',
          type: 'multiple-choice-multi-select',
          question: 'Other cancer treatment',
          options: [
            { key: 'past', displayName: 'Past' },
            { key: 'current', displayName: 'Current' },
            { key: 'never', displayName: ' Never', mutuallyExclusive: true },
            { key: 'noAnswer', displayName: 'Prefer not to answer', mutuallyExclusive: true }
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
          key: 'cancerTreatmentOtherSelectionPastSpecified',
          type: 'text',
          question: 'Please specify what other cancer(s) you have had in the past.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersTreatments');
            const question = group?.questions.find(x => x.key === 'cancerTreatmentOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('past')) ?? false;
          }
        },
        {
          key: 'cancerTreatmentOtherSelectionCurrentSpecified',
          type: 'text',
          question: 'Please specify what other cancer(s) you have currently.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersTreatments');
            const question = group?.questions.find(x => x.key === 'cancerTreatmentOtherSelection');
            return (Array.isArray(question?.value) && question.value.includes('current')) ?? false;
          }
        }
      ]
    },
    {
      key: 'medicalHistoryOtherHealthConditions',
      title: 'Medical History - Other conditions',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Have you ever been diagnosed by a doctor with any of these OTHER health conditions?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you have never had the condition, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'atrialFibrillation',
          type: 'multiple-choice',
          question: 'Atrial fibrillation',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'chronicPericarditis',
          type: 'multiple-choice',
          question: 'Chronic pericarditis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'congestiveHeartFailure',
          type: 'multiple-choice',
          question: 'Congestive heart failure',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'coronaryHeartDisease',
          type: 'multiple-choice',
          question: 'Coronary heart disease',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'chronicKidneyDisease',
          type: 'multiple-choice',
          question: 'Chronic kidney disease',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'obesity',
          type: 'multiple-choice',
          question: 'Obesity',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'obesityCurrently',
          type: 'multiple-choice',
          question: 'Are you currently considered obese?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryOtherHealthConditions');
            const question = group?.questions.find(x => x.key === 'obesity');
            return question?.value === 'yes';
          }
        },
        {
          key: 'scoliosis',
          type: 'multiple-choice',
          question: 'Scoliosis',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'diabetesCurrently',
          type: 'multiple-choice',
          question: 'Do you currently have diabetes?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            {key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          className: 'section-divider'
        },
        {
          key: 'diabetesTypeCurrently',
          type: 'multiple-choice',
          question: 'Which type of diabetes do you have?',
          options: [
            { key: 'type1', displayName: 'Type 1' },
            { key: 'type2', displayName: 'Type 2' },
            { key: 'gestationalDiabetes', displayName: 'Gestational diabetes' },
            { key: 'ladaType3a', displayName: 'LADA/Type 3a' },
            { key: 'notCertain', displayName: 'Not certain' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryOtherHealthConditions');
            const question = group?.questions.find(x => x.key === 'diabetesCurrently');
            return question?.value === 'yes';
          }
        },
        {
          key: 'heartDiseaseOtherCurrently',
          type: 'multiple-choice',
          question: 'Do you currently have any other heart disease(s)?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'heartDiseaseOtherCurrentlySpecified',
          type: 'text',
          question: 'Please specify what other heart condition(s) you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryOtherHealthConditions');
            const question = group?.questions.find(x => x.key === 'heartDiseaseOtherCurrently');
            return question?.value === 'yes';
          }
        },
        {
          key: 'infectiousDiseaseCurrently',
          type: 'multiple-choice',
          question: 'Do you currently have any infectious disease(s)?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'infectiousDiseaseCurrentlySpecified',
          type: 'text',
          question: 'Please specify what other infectious disease(s) you have.',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryOtherHealthConditions');
            const question = group?.questions.find(x => x.key === 'infectiousDiseaseCurrently');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'medicalHistoryMedicationsOrSubstances',
      title: 'Medical History - Medications',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Do you currently take or use any of these medications or substances to treat ANY condition (e.g. using antidepressant to treat ADHD)?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “No”. Please only change the responses that apply to you. If you are not currently taking the medication, you can leave the response as it is.
          </Typography>
        </React.Fragment>
      ),
      questions: [
        {
          key: 'medicationAntiAnxiety',
          type: 'multiple-choice',
          question: 'Anti-anxiety medication',
          hint: 'Ativan, Benzodiazepine, Klonopin, Valium, Xanax, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationAntibiotics',
          type: 'multiple-choice',
          question: 'Antibiotics',
          hint: 'Amoxicillin, Augmentin, Clindamycin, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationAnticholergenics',
          type: 'multiple-choice',
          question: 'Anticholergenics',
          hint: 'Atropine, Benztropine, Diphenhydramine, Oxybutynin, Scopolamine, Trihexyphenidyl, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationAntidepressants',
          type: 'multiple-choice',
          question: 'Antidepressants',
          hint: 'Amitriptyline, Lexapro, Prozac, Zoloft, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationAntiepileptics',
          type: 'multiple-choice',
          question: 'Antiepileptics',
          hint: 'Depakote, Phenytoin, Topamax, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationAllergy',
          type: 'multiple-choice',
          question: 'Allergy medications (antihistamines)',
          hint: 'Allegra, Benadryl, Claritin, Zyrtec, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationBloodPressure',
          type: 'multiple-choice',
          question: 'Blood pressure medications (anti-hypertensive)',
          hint: 'Lisinopril, Metoprolol, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationPain',
          type: 'multiple-choice',
          question: 'Pain medications',
          hint: 'Analgesics, NSAIDs, Opiates, Neuro-modulators, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true,
        },
        {
          key: 'medicationDecongestants',
          type: 'multiple-choice',
          question: 'Decongestants',
          hint: 'Afrin, Mucinex, Sudafed, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationDiuretics',
          type: 'multiple-choice',
          question: 'Diuretics',
          hint: 'Lasix, Hydrochlorothiazide, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true,
        },
        {
          key: 'medicationHormones',
          type: 'multiple-choice',
          question: 'Hormones',
          hint: 'Thyroid medications, birth control, HRT or androgenic steroids, insulin, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationImmuneSuppressors',
          type: 'multiple-choice',
          question: 'Immune suppressors',
          hint: 'Humira, Imuran, Methotrexate, Remicade, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationInhaledCorticosteroids',
          type: 'multiple-choice',
          question: 'Inhaled corticosteroids',
          hint: 'Alvesco, Asmanex Twisthaler, Flovent, Fluticasone, Pulmicort, Symbicort, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationMuscleRelaxants',
          type: 'multiple-choice',
          question: 'Muscle relaxants',
          hint: 'Amrix, Baclofen, Flexeril, Robaxin, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationOralSteroids',
          type: 'multiple-choice',
          question: 'Oral steroids',
          hint: 'Hydrocortisone, Prednisone, Z-pak, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationPsychotropicAntipsychotic',
          type: 'multiple-choice',
          question: 'Psychotropic/antipsychotic medications',
          hint: 'Abilify, Clozapine, Invega, Risperidone, Seroquel, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationReflux',
          type: 'multiple-choice',
          question: 'Reflux medications',
          hint: 'Nexium, Omeprazole, Pantoprazole, Pepcid, Prilosec, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationStimulants',
          type: 'multiple-choice',
          question: 'Stimulants',
          hint: 'Adderall, Ritalin, Vyvanse, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'medicalHistoryPainMedications',
      title: 'Medical History - Pain Medications',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Which Pain Medication(s) do you currently take?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “No”. Please only change the responses that apply to you. If you are not currently taking the medication, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryMedicationsOrSubstancesGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryMedicationsOrSubstances');
        const painMedicationQuestion = medicalHistoryMedicationsOrSubstancesGroup?.questions.find(q => q.key === 'medicationPain');
        return painMedicationQuestion?.value === 'yes';
      },
      questions: [
        {
          key: 'medicationPainAnalgesics',
          type: 'multiple-choice',
          question: 'Analgesics',
          hint: 'Acetaminophen, Excedrin, Tylenol, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationPainNSAIDs',
          type: 'multiple-choice',
          question: 'NSAIDs',
          hint: 'Advil, Celebrex, Ibuprofen, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationPainOpiates',
          type: 'multiple-choice',
          question: 'Opiates',
          hint: 'Fentanyl, Morphine, Oxycodone, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationPainNeuroModulators',
          type: 'multiple-choice',
          question: 'Neuro-modulators',
          hint: 'Gabapentin, Lyrica, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'medicalHistoryHormoneMedications',
      title: 'Medical History - Hormone Medications',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Which Hormone(s) do you currently take?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “No”. Please only change the responses that apply to you. If you are not currently taking the medication, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      condition: (questionnaire: Questionnaire) => {
        const medicalHistoryMedicationsOrSubstancesGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryMedicationsOrSubstances');
        const hormoneMedicationQuestion = medicalHistoryMedicationsOrSubstancesGroup?.questions.find(q => q.key === 'medicationHormones');
        return hormoneMedicationQuestion?.value === 'yes';
      },
      questions: [
        {
          key: 'medicationHormonesAndrogenicSteroids',
          type: 'multiple-choice',
          question: 'Androgenic steroids',
          hint: 'Androgel, Danazol, Methyltestosterone, Testosterone, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesHrt',
          type: 'multiple-choice',
          question: 'Hormonal replacement therapy (HRT)',
          hint: 'Alora, Climara, Estrace, Estrogen gel, Menostar, Premarin, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesInsulin',
          type: 'multiple-choice',
          question: 'Insulin',
          hint: 'Humalog, Lantis, Novalog, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesContraceptives',
          type: 'multiple-choice',
          question: 'Contraceptive, birth control',
          hint: 'Depo-Provera, IUD, Nexplanon, NuvaRing, Yasmin, Yaz, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesThyroid',
          type: 'multiple-choice',
          question: 'Thyroid replacement',
          hint: 'Depo-Provera, IUD, Nexplanon, NuvaRing, Yasmin, Yaz, etc.',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesOther',
          type: 'multiple-choice',
          question: 'Other hormone replacement',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'medicationHormonesOtherSpecified',
          type: 'text',
          question: 'What other hormone replacement(s) do you currently take?',
          placeholder: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalHistoryHormoneMedications');
            const question = group?.questions.find(x => x.key === 'medicationHormonesOther');
            return question?.value === 'yes';
          }
        }
      ]
    },
    {
      key: 'medicalHistorySymptoms',
      title: 'Medical History - Symptoms',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Are you experiencing any of the following symptoms today?
          </Typography>
          <Typography variant="body1">
            To save you time, the responses are pre-set to “NO”. Please only change the responses that apply to you. If you are not experiencing the symptom, you can leave the response as it is.
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'anxietyNervousness',
          type: 'multiple-choice',
          question: 'Anxiety or nervousness',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'confusion',
          type: 'multiple-choice',
          question: 'Confusion',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'coughingOrClearingThroat',
          type: 'multiple-choice',
          question: 'Coughing or clearing your throat',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'dizziness',
          type: 'multiple-choice',
          question: 'Dizziness',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'headacheMigraine',
          type: 'multiple-choice',
          question: 'Headache or migraine',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'nasalCongestionObstruction',
          type: 'multiple-choice',
          question: 'Nasal congestion or obstruction',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'scratchySoreThroat',
          type: 'multiple-choice',
          question: 'Scratchy or sore throat',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'shortnessOfBreath',
          type: 'multiple-choice',
          question: 'Shortness of breath',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'sleepDisturbance',
          type: 'multiple-choice',
          question: 'Sleep disturbance',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
        {
          key: 'speechDifficulty',
          type: 'multiple-choice',
          question: 'Speech difficulty',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' }
          ],
          value: 'no',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'medicalhistoryGynecological',
      title: 'Medical History - Gynecological',
      questions: [
        {
          key: 'menstruation',
          type: 'multiple-choice',
          question: 'Do you currently have periods (with regular or irregular cycles)?',
          options: [
            { key: 'doesNotApply', displayName: 'Does not apply (i.e. I am a male who has never had a period)' },
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No, but I used to' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' },
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'menstruationYesMenstrualCycle',
          type: 'multiple-choice',
          question: 'Where in your cycle are you? We ask because this may affect your voice.',
          options: [
            { key: 'menstruating', displayName: 'Menstruating' },
            { key: 'premenstrual', displayName: 'Premenstrual' },
            { key: 'postmenstrual', displayName: 'Postmenstrual' },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalhistoryGynecological');
            const question = group?.questions.find(x => x.key === 'menstruation');
            return question?.value === 'yes';
          }
        },
        {
          key: 'menstruationNo',
          type: 'multiple-choice',
          question: 'Please explain',
          options: [
            { key: 'pregnant', displayName: 'I am currently pregnant' },
            { key: 'birthControl', displayName: `I'm using birth control` },
            { key: 'menopause', displayName: 'I have undergone menopause or have had a hysterectomy' },
            { key: 'hormoneTherapy', displayName: 'I no longer have my period due to hormone therapy' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' },
            {
              key: 'other', displayName: 'Other', followUpQuestions: [
                {
                  key: 'menstruationNoOtherSpecified',
                  type: 'text',
                  question: '',
                  placeholder: 'Please specify',
                  value: '',
                  defaultValue: '',
                  required: true
                }
              ]
            },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalhistoryGynecological');
            const question = group?.questions.find(x => x.key === 'menstruation');
            return question?.value === 'no';
          }
        },
        {
          key: 'menstruationNoPregnantTrimester',
          type: 'multiple-choice',
          question: 'Which trimester of pregnancy are you in?',
          options: [
            { key: 'firstTrimester', displayName: 'First trimester (less than 14 weeks)' },
            { key: 'secondTrimester', displayName: 'Second trimester (14-28 weeks)' },
            { key: 'thirdTrimester', displayName: 'Third trimester (more than 28 weeks)' },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'medicalhistoryGynecological');
            const question = group?.questions.find(x => x.key === 'menstruationNo');
            return question?.value === 'pregnant';
          }
        },
      ]
    },
    {
      key: 'physicalHealthHeightAndWeight',
      title: 'Physical Health - Height and Weight',
      content: (
        <React.Fragment>
          <Typography variant="body1">
          Some aspects of your physical health can affect the sound of your voice. This section asks questions about some of these factors.
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'unit',
          type: 'multiple-choice',
          question: 'Unit',
          options: [
            { key: '1', displayName: 'Metric' },
            { key: '2', displayName: 'US Customary units' }
          ],
          value: '2',
          defaultValue: '',
          required: true,
        },
        {
          key: 'height',
          type: 'height',
          question: 'Height',
          unit: '1',
          value: '0',
          defaultValue: '',
          required: true,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'physicalHealthHeightAndWeight');
            const heightQuestion = group?.questions.find(x => x.key === 'height');
            const heightInInches = parseInt((heightQuestion?.value || '0') as string);
            return heightInInches >= 20;
          }
        },
        {
          key: 'weight',
          type: 'weight',
          question: 'Weight (lbs)',
          placeholder: 'Enter weight',
          unit: '1',
          value: '0',
          defaultValue: '',
          required: true,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'physicalHealthHeightAndWeight');
            const weightQuestion = group?.questions.find(x => x.key === 'weight');
            const weightInPounds = parseInt((weightQuestion?.value || '0') as string);
            return weightInPounds > 0;
          }
        },
      ]
    },
    {
      key: 'physicalHealthOverallHealth',
      title: 'Physical Health - Overall Health',
      description: '',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            This questionnaire asks about difficulties due to health conditions. Health conditions include diseases or illnesses, other health problems that may be short or long lasting, injuries, mental or emotional problems, and problems with alcohol or drugs.
          </Typography>
          <Typography variant="body1" style={{ marginTop: '1em' }}>
            Think back over the past 30 days and answer these questions, thinking about how much difficulty you had doing the following activities. For each question, please choose only one response.
          </Typography>
          <Typography variant="body1" style={{ marginTop: '1em' }}>
            In the past 30 days, how much difficulty did you have in:
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'whodasStanding',
          type: 'multiple-choice',
          question: 'Standing for long periods such as 30 minutes?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasTakeCare',
          type: 'multiple-choice',
          question: 'Taking care of your household responsibilities?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasLearnTask',
          type: 'multiple-choice',
          question: 'Learning a new task, for example, learning how to get to a new place?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasProblemJoin',
          type: 'multiple-choice',
          question: 'How much of a problem did you have joining in community activities (for example, festivities, religious or other activities) in the same way as anyone else can?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasHealthProblem',
          type: 'multiple-choice',
          question: 'How much have you been emotionally affected by your health problems?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasConcentrating',
          type: 'multiple-choice',
          question: 'Concentrating on doing something for ten minutes?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasWalking',
          type: 'multiple-choice',
          question: 'Walking a long distance such as a kilometre [or equivalent]?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasWashing',
          type: 'multiple-choice',
          question: 'Washing your whole body?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasGetDressed',
          type: 'multiple-choice',
          question: 'Getting dressed?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasDealingPeople',
          type: 'multiple-choice',
          question: 'Dealing with people you do not know?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasFriendship',
          type: 'multiple-choice',
          question: 'Maintaining a friendship?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasDayToDayWork',
          type: 'multiple-choice',
          question: 'Your day-to-day work?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'mild', displayName: 'Mild' },
            { key: 'moderate', displayName: 'Moderate' },
            { key: 'severe', displayName: 'Severe' },
            { key: 'extreme', displayName: 'Extreme or cannot do' },
          ],
          value: '',
          defaultValue: '',
          required: true,
        },
        {
          key: 'whodasDaysDifficultiesPresent',
          type: 'integer',
          question: 'Overall, in the past 30 days, how many days were these difficulties present?',
          placeholder: 'Enter number of days',
          value: '0',
          defaultValue: '',
          required: true,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'physicalHealthOverallHealth');
            const question = group?.questions.find(x => x.key === 'whodasDaysDifficultiesPresent');
            const value = parseInt(question?.value as string || '0');
            return value >= 0 && value <= 30;
          }
        },
        {
          key: 'whodasDaysUnable',
          type: 'integer',
          question: 'In the past 30 days, for how many days were you totally unable to carry out your usual activities or work because of any health condition?',
          placeholder: 'Enter number of days',
          value: '0',
          defaultValue: '',
          required: true,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'physicalHealthOverallHealth');
            const question = group?.questions.find(x => x.key === 'whodasDaysUnable');
            const value = parseInt(question?.value as string || '0');
            return value >= 0 && value <= 30;
          }
        },
        {
          key: 'whodasDaysReducedActivity',
          type: 'integer',
          question: 'In the past 30 days, not counting the days that you were totally unable, for how many days did you cut back or reduce your usual activities or work because of any health condition?',
          placeholder: 'Enter number of days',
          value: '0',
          defaultValue: '',
          required: true,
          validation: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'physicalHealthOverallHealth');
            const question = group?.questions.find(x => x.key === 'whodasDaysReducedActivity');
            const value = parseInt(question?.value as string || '0');
            return value >= 0 && value <= 30;
          }
        },
      ]
    },
    {
      key: 'dayToDayActivitiesVoiceUse',
      title: 'Day To Day Activities - Voice Use',
      description: '',
      content: (
        <React.Fragment>
          <Typography variant="body1">
            Things you do on a daily basis (including your job, work environment, hydration, etc.) can affect the sound of your voice OR affect the way you complete the tasks in this study. This section asks questions about some of these factors.
          </Typography>
        </React.Fragment>
        ),
      questions: [
        {
          key: 'voiceActivitySelection',
          type: 'multiple-choice-multi-select',
          question: 'Do you have a job or hobby that requires using a loud voice for many hours a day? (Check all that apply):',
          options: [
            { key: 'administrativeAssistant', displayName: 'Administrative assistant' },
            { key: 'attorney', displayName: 'Attorney' },
            { key: 'bartender', displayName: 'Bartender' },
            { key: 'barista', displayName: 'Barista' },
            { key: 'callCenterWorker', displayName: 'Call center worker' },
            { key: 'cheerleader', displayName: 'Cheerleader' },
            { key: 'childCareProvider', displayName: 'Child-care provider' },
            { key: 'exerciseInstructor', displayName: 'Exercise instructor' },
            { key: 'ministerPreacher', displayName: 'Minister/Preacher' },
            { key: 'parentGuardianCaregiver', displayName: 'Parent, guardian, or caregiver' },
            { key: 'publicSpeaker', displayName: 'Public Speaker' },
            { key: 'receptionist', displayName: 'Receptionist' },
            { key: 'salesperson', displayName: 'Salesperson' },
            { key: 'singer', displayName: 'Singer' },
            { key: 'teacher', displayName: 'Teacher ' },
            { key: 'waiter', displayName: 'Waiter' },
            { key: 'none', displayName: 'NONE OF THE ABOVE', mutuallyExclusive: true },
            { key: 'other', displayName: 'Other'},
          ],
          value: [],
          defaultValue: [],
          required: true
        },
        {
            key: 'voiceActivitySelectionOtherSpecified',
            type: 'text',
            question: 'Please specify what other job or hobby you have that requires you to use your voice loudly for many hours a day.',
            value: '',
            defaultValue: '',
            required: true,
            condition: (questionnaire: Questionnaire) => {
              const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesVoiceUse');
              const question = group?.questions.find(x => x.key === 'voiceActivitySelection');
              return Array.isArray(question?.value) && question.value.includes('other');
            }
          },
        {
          key: 'numberOfHours',
          type: 'integer',
          question: 'How many hours per day do you do this activity with a loud voice or in a loud environment that requires elevating your voice (for instance, a noisy bar or a noisy classroom)?',
          placeholder: 'Enter number',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesVoiceUse');
            const question = group?.questions.find(x => x.key === 'voiceActivitySelection');
            return Array.isArray(question?.value) && question.value.length > 0 && !question.value.includes('none');
          }
        },
      ]
    },
    {
      key: 'dayToDayReadingAbility',
      title: 'Day To Day Activities - Reading Ability',
      questions: [
        {
          key: 'readingAbility',
          type: 'multiple-choice',
          question: 'How good do you think you are at reading out loud in English-that is, reading out loud at a normal rate without making mistakes and understanding what you read?',
          options: [
            { key: 'excellent', displayName: 'Excellent' },
            { key: 'veryGood', displayName: 'Very good' },
            { key: 'good', displayName: 'Good' },
            { key: 'fair', displayName: 'Fair' },
            { key: 'poor', displayName: 'Poor' }
          ],
          value: '',
          defaultValue: '',
          required: true
        }
      ]
    },
    {
      key: 'dayToDayActivitiesTiredness',
      title: 'Day To Day Activities - Tiredness',
      questions: [
        {
          key: 'tiredness',
          type: 'multiple-choice',
          question: 'How tired are you today?',
          hint: '0=not tired at all, 10=extremely tired',
          options: [
            { key: '0', displayName: '0' },
            { key: '1', displayName: '1' },
            { key: '2', displayName: '2' },
            { key: '3', displayName: '3' },
            { key: '4', displayName: '4' },
            { key: '5', displayName: '5' },
            { key: '6', displayName: '6' },
            { key: '7', displayName: '7' },
            { key: '8', displayName: '8' },
            { key: '9', displayName: '9' },
            { key: '10', displayName: '10' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'dayToDayActivitiesHydration',
      title: 'Day To Day Activities - Hydration',
      questions: [
        {
          key: 'hydrationConsumptionTypical',
          type: 'integer',
          question: 'On a typical day, how many small (8oz or 230mL) cups of water do you drink?',
          value: '0',
          defaultValue: '',
          required: true
        },
        {
          key: 'hydrationConsumptionToday',
          type: 'integer',
          question: 'Today, how many small (8oz or 230mL) cups of water have you had?',
          value: '0',
          defaultValue: '',
          required: true
        },
      ]
    },
    {
      key: 'dayToDayActivitiesCaffeineIntake',
      title: 'Day To Day Activities - Caffeine Intake',
      questions: [
        {
          key: 'caffeineTypical',
          type: 'multiple-choice',
          question: 'On a typical day, do you drink coffee or use other caffeinated products?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'caffeineTypicalProductSelection',
          type: 'multiple-choice-multi-select',
          question: 'What type(s) of caffeinated products do you typically consume?',
          options: [
            { key: 'caffeinatedTeas', displayName: 'Caffeinated teas' },
            { key: 'carbonatedBeverages', displayName: 'Carbonated beverages (e.g. soda, pop. Coke, etc.)' },
            { key: 'coffee', displayName: 'Coffee' },
            { key: 'energyDrinks', displayName: 'Energy drinks' },
            { key: 'espresso', displayName: 'Espresso' },
            { key: 'other', displayName: 'Other' },
          ],
          value: [],
          defaultValue: [],
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineTypical');
            return question?.value === 'yes';
          }
        },
        {
          key: 'caffeineTypicalProductSelectionOtherSpecified',
          type: 'text',
          question: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineTypicalProductSelection');
            return Array.isArray(question?.value) && question.value.includes('other');
          }
        },
        {
          key: 'caffeineTypicalServings',
          type: 'integer',
          question: 'On a typical day, how many servings of these caffeinated products do you typically consume?',
          hint: 'One serving is 8 oz or 240 mL of coffee, tea, or energy drinks, 1 oz or 30 mL of espresso, 12 oz or 360 mL of soda',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineTypical');
            return question?.value === 'yes';
          }
        },
        {
          key: 'caffeineToday',
          type: 'multiple-choice',
          question: 'Today, have you had coffee or any other caffeinated products?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'caffeineTodayProductSelection',
          type: 'multiple-choice-multi-select',
          question: 'What type(s) of caffeinated products have you had today?',
          options: [
            { key: 'caffeinatedTeas', displayName: 'Caffeinated teas' },
            { key: 'carbonatedBeverages', displayName: 'Carbonated beverages (e.g. soda, pop. Coke, etc.)' },
            { key: 'coffee', displayName: 'Coffee' },
            { key: 'energyDrinks', displayName: 'Energy drinks' },
            { key: 'espresso', displayName: 'Espresso' },
            { key: 'other', displayName: 'Other' },
          ],
          value: [],
          defaultValue: [],
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineToday');
            return question?.value === 'yes';
          }
        },
                {
          key: 'caffeineTodayProductSelectionOtherSpecified',
          type: 'text',
          question: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineTodayProductSelection');
            return Array.isArray(question?.value) && question.value.includes('other');
          }
        },
        {
          key: 'caffeineTodayServings',
          type: 'integer',
          question: 'How many servings of these caffeinated products have you had today?',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesCaffeineIntake');
            const question = group?.questions.find(x => x.key === 'caffeineToday');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'dayToDayActivitiesNicotineUse',
      title: 'Day To Day Activities - Nicotine use',
      questions: [
        {
          key: 'nicotineProductSelection',
          type: 'multiple-choice-multi-select',
          question: 'Have you ever used any of the following nicotine (tobacco) products? Please check all that apply.',
          options: [
            { key: 'neverUsedNicotineProduct', displayName: 'I have never used a nicotine product', mutuallyExclusive: true },
            { key: 'cigaretteCigar', displayName: 'Cigarette or cigar' },
            { key: 'electronicNicotineProduct', displayName: 'Electronic nicotine product (e.g. e-cigarettes, vape pens, hookah pens, personal vaporizers, e-cigars, e-pipes, e-hookahs, etc.)' },
            { key: 'nicotinePatch', displayName: 'Nicotine patch' },
            { key: 'nicotineGumChew', displayName: 'Nicotine gum or chew' },
            { key: 'other', displayName: 'Other' },
          ],
          value: [],
          defaultValue: [],
          required: false
        },
        {
          key: 'nicotineProductSelectionOtherSpecified',
          type: 'text',
          question: 'Please specify',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineProductSelection');
            return Array.isArray(question?.value) && question.value.includes('other');
          }
        },
        {
          key: 'nicotineMoreThan100InLife',
          type: 'multiple-choice',
          question: 'Have you used any nicotine products at least 100 times in your entire life?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineProductSelection');
            return Array.isArray(question?.value) && question.value.length > 0 && !question.value.includes('neverUsedNicotineProduct');
          }
        },
        {
          key: 'nicotineFrequency',
          type: 'multiple-choice',
          question: 'Do you currently use any nicotine products every day, some days, or not at all?',
          options: [
            { key: 'everyDay', displayName: 'Every day' },
            { key: 'someDays', displayName: 'Some days' },
            { key: 'notAtAll', displayName: 'Not at all' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineProductSelection');
            return Array.isArray(question?.value) && question.value.length > 0 && !question.value.includes('neverUsedNicotineProduct');
          }
        },
        {
          key: 'nicotineAgeStarted',
          type: 'multiple-choice',
          question: 'How old were you when you first started regularly using nicotine products?',
          options: [
            // Note: "Age:" is not working in translations, so changed to "Enter age:"
            { key: 'age', displayName: 'Enter age:', followUpQuestions: [
              {
                key: 'nicotineAgeStartedSpecified',
                type: 'integer',
                question: '',
                value: '',
                defaultValue: '',
                required: true
              }
            ] },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineMoreThan100InLife');
            return question?.value === 'yes';
          }
        },
        {
          key: 'nicotineAgeEnded',
          type: 'multiple-choice',
          question: 'If you have completely stopped using nicotine, about how old were you when you stopped?',
          options: [
            { key: 'age', displayName: 'Age when you stopped using:', followUpQuestions: [
              {
                key: 'nicotineAgeEndedSpecified',
                type: 'integer',
                question: '',
                placeholder: 'Enter age',
                value: '',
                defaultValue: '',
                required: true
              }
            ] },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineFrequency');
            return question?.value === 'notAtAll';
          }
        },
        {
          key: 'nicotineYearsUsed',
          type: 'multiple-choice',
          question: 'For how many years have you used nicotine?',
          options: [
            { key: 'lessThanOneYear', displayName: 'Less than 1 year'},
            { key: 'numberOfYears', displayName: 'Number of years:', followUpQuestions: [
              {
                key: 'nicotineYearsUsedSpecified',
                type: 'integer',
                question: '',
                placeholder: 'Enter number',
                value: '',
                defaultValue: '',
                required: true
              }
            ] },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineFrequency');
            return question?.value === 'notAtAll';
          }
        },
        {
          key: 'nicotineFrequencyDailyCurrently',
          type: 'multiple-choice',
          question: 'On average, how many times do you use nicotine per day now?',
          options: [
            { key: 'lessThanOncePerDay', displayName: 'Less than once per day'},
            { key: 'numberUses', displayName: 'Number of uses per day:', followUpQuestions: [
              {
                key: 'nicotineFrequencyDailyCurrentlySpecified',
                type: 'integer',
                question: '',
                placeholder: 'Enter number',
                value: '',
                defaultValue: '',
                required: true
              }
            ] },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineFrequency');
            return question?.value === 'everyDay' || question?.value === 'someDays';
          }
        },
        {
          key: 'nicotineFrequencyDailyAverage',
          type: 'multiple-choice',
          question: 'On average, over the entire time that you used nicotine, how many times each day did you use nicotine?',
          options: [
            { key: 'lessThanOncePerDay', displayName: 'Less than once per day'},
            { key: 'numberUses', displayName: 'Number of uses per day:', followUpQuestions: [
              {
                key: 'nicotineFrequencyDailyAverageSpecified',
                type: 'integer',
                question: '',
                placeholder: 'Enter number',
                value: '',
                defaultValue: '',
                required: true
              }
            ] },
            { key: 'notCertain', displayName: 'Not certain' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesNicotineUse');
            const question = group?.questions.find(x => x.key === 'nicotineMoreThan100InLife');
            return question?.value === 'yes';
          }
        },
      ]
    },
    {
      key: 'dayToDayActivitiesAlcoholConsumption',
      title: 'Day To Day Activities - Alcohol consumption',
      questions: [
        {
          key: 'alcoholUse',
          type: 'multiple-choice',
          question: 'Do you drink alcohol?',
          hint: '(Drinks can be beer, wine, shots of liquor, cocktails containing a shot of liquor)',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'notCurrently', displayName: 'Not currently, but I have in the past' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' },
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'alcoholFrequency',
          type: 'multiple-choice',
          question: 'How often do you have at least one drink containing alcohol?',
          options: [
            { key: 'monthlyOrLess', displayName: 'Monthly or less' },
            { key: 'twoToFourTimesAMonth', displayName: '2-4 times a month' },
            { key: 'twoToThreeTimesAWeek', displayName: '2-3 times a week' },
            { key: 'fourOrMoreTimesAWeek', displayName: '4 or more times a week' },
            { key: 'notToAnswer', displayName: 'Prefer not to answer' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes';
          }
        },
        {
          key: 'alcoholFrequencyDailyCurrently',
          type: 'multiple-choice',
          question: 'How many drinks containing alcohol do you have on a typical day when you are drinking?',
          hint: 'One drink is 12 oz. or 330 mL of beer, 5 oz. or 150 mL of wine, 1.5 oz. or 45mL (one shot) of liquor',
          options: [
            { key: 'twoOrFewer', displayName: '2 or fewer' },
            { key: 'threeToFour', displayName: '3 - 4' },
            { key: 'fiveToSix', displayName: '5 - 6' },
            { key: 'sevenToNine', displayName: '7 - 9' },
            { key: 'tenOrMore', displayName: '10 or more' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes';
          }
        },
        {
          key: 'alcoholSixOrMoreDrinksAtOnceLastYear',
          type: 'multiple-choice',
          question: 'How often did you have six or more drinks on one occasion in the past year?',
          options: [
            { key: 'neverInThePastYear', displayName: 'Never in the past year' },
            { key: 'lessThanMonthly', displayName: 'Less than monthly' },
            { key: 'monthly', displayName: 'Monthly' },
            { key: 'weekly', displayName: 'Weekly' },
            { key: 'daily', displayName: 'Daily or almost daily' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes';
          }
        },
        {
          key: 'alcoholToday',
          type: 'multiple-choice',
          question: 'Have you drunk alcohol today?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes';
          }
        },
        {
          key: 'alcoholDrinksToday',
          type: 'integer',
          question: 'How many drinks have you had today?',
          placeholder: 'Enter number',
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholToday');
            return question?.value === 'yes';
          }
        },
        {
          key: 'alcoholInRecoveryCurrently',
          type: 'multiple-choice',
          question: 'Are you currently in recovery for alcohol use?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes' || question?.value === 'notCurrently';
          }
        },
        {
          key: 'alcoholInRecoveryEver',
          type: 'multiple-choice',
          question: 'Have you ever been in rehab or counseling for heavy alcohol use?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesAlcoholConsumption');
            const question = group?.questions.find(x => x.key === 'alcoholUse');
            return question?.value === 'yes' || question?.value === 'notCurrently';
          }
        },
      ]
    },
    {
      key: 'dayToDayActivitiesOtherSubstanceUse',
      title: 'Day To Day Activities - Other Substance use',
      questions: [
        {
          key: 'drugUseOnYourOwn',
          type: 'multiple-choice',
          question: `Have you ever used medicines ON YOUR OWN without a doctor's prescription, or in greater amounts or longer than prescribed?`,
          options: [
            { key: 'no', displayName: 'No' },
            { key: 'inThePast', displayName: 'Not this year, but I have in the past' },
            { key: 'oneOrMore', displayName: 'One or more times this year' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'drugUse',
          type: 'multiple-choice',
          question: 'Have you ever used marijuana, cocaine or crack, and/or other drugs?',
          options: [
            { key: 'no', displayName: 'No' },
            { key: 'inThePast', displayName: 'Not this year, but I have in the past' },
            { key: 'oneOrMore', displayName: 'One or more times this year' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true
        },
        {
          key: 'drugSmokedMarijuanaSmokedPast3MonthFrequency',
          type: 'multiple-choice',
          question: 'In the PAST THREE MONTHS, how often have you SMOKED marijuana (e.g. cannabis, pot, grass, hash, weed, etc.) either recreationally or as prescribed by a doctor?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'onceOrTwice', displayName: 'Once or twice' },
            { key: 'monthly', displayName: 'Monthly' },
            { key: 'weekly', displayName: 'Weekly' },
            { key: 'daily', displayName: 'Daily or almost daily' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUse');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'drugEdibleMarijuanaPast3MonthFrequency',
          type: 'multiple-choice',
          question: 'In the PAST THREE MONTHS, how often have you taken marijuana as an EDIBLE either recreationally or as prescribed by a doctor?',
          options: [
            { key: 'none', displayName: 'None' },
            { key: 'onceOrTwice', displayName: 'Once or twice' },
            { key: 'monthly', displayName: 'Monthly' },
            { key: 'weekly', displayName: 'Weekly' },
            { key: 'daily', displayName: 'Daily or almost daily' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUse');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substancePainkillers',
          type: 'multiple-choice',
          content:
            <>
              <Typography variant="h6" component='div' sx={{ marginBottom: '8px' }}>
                During the past TWO (2) WEEKS, about how often did you use any of the following medicines ON YOUR OWN, that is, without a doctor's prescription, in greater amounts or longer than prescribed?
              </Typography>
            </>
          ,
          question: 'Painkillers (like Vicodin)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceStimulants',
          type: 'multiple-choice',
          question: 'Stimulants (like Ritalin, Adderall)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceSedatives',
          type: 'multiple-choice',
          question: 'Sedatives or tranquilizers (like sleeping pills or Valium)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceMarijuana',
          type: 'multiple-choice',
          question: 'Marijuana',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceCocaine',
          type: 'multiple-choice',
          question: 'Cocaine or crack',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceClubDrugs',
          type: 'multiple-choice',
          question: 'Club drugs (like ecstasy)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceHallucinogens',
          type: 'multiple-choice',
          question: 'Hallucinogens (like LSD)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceHeroin',
          type: 'multiple-choice',
          question: 'Heroin or other opioids, including synthetic opioids like fentanyl',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceInhalants',
          type: 'multiple-choice',
          question: 'Inhalants or solvents (like glue)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceMethamphetamine',
          type: 'multiple-choice',
          question: 'Methamphetamine (like speed)',
          options: [
            { key: 'none', displayName: 'Not at all' },
            { key: 'oneOrTwoDays', displayName: 'One or two days' },
            { key: 'severalDays', displayName: 'Several days' },
            { key: 'moreThanHalfTheDays', displayName: 'More than half the days' },
            { key: 'nearlyEveryDay', displayName: 'Nearly every day' },
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const question = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            return question?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceInRecoveryCurrently',
          type: 'multiple-choice',
          question: 'Are you currently in recovery for substance use?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const drugUseOnYourOwnQuestion = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            const drugUseQuestion = group?.questions.find(x => x.key === 'drugUse');
            return drugUseOnYourOwnQuestion?.value === 'inThePast' || drugUseOnYourOwnQuestion?.value === 'oneOrMore' ||
              drugUseQuestion?.value === 'inThePast' || drugUseQuestion?.value === 'oneOrMore';
          }
        },
        {
          key: 'substanceInRecoveryEver',
          type: 'multiple-choice',
          question: 'Have you ever been in rehab or counseling for heavy substance use?',
          options: [
            { key: 'yes', displayName: 'Yes' },
            { key: 'no', displayName: 'No' },
            { key: 'noAnswer', displayName: 'Prefer not to answer' }
          ],
          value: '',
          defaultValue: '',
          required: true,
          condition: (questionnaire: Questionnaire) => {
            const group = questionnaire.groups.find(x => x.key === 'dayToDayActivitiesOtherSubstanceUse');
            const drugUseOnYourOwnQuestion = group?.questions.find(x => x.key === 'drugUseOnYourOwn');
            const drugUseQuestion = group?.questions.find(x => x.key === 'drugUse');
            return drugUseOnYourOwnQuestion?.value === 'inThePast' || drugUseOnYourOwnQuestion?.value === 'oneOrMore' ||
              drugUseQuestion?.value === 'inThePast' || drugUseQuestion?.value === 'oneOrMore';
          }
        },
      ]
    },
  ]
};

export const ConfoundersDefinition = Definition;

export const Confounders = ({onCompletion}: {onCompletion: () => void}) => {
  const { storage } = useStorage();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(() => ({
    id: 'confounders',
    ...Definition,
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'in-progress',
  }));

  const participantDisorders = [
    ...storage.participant.disorderInformation.selectedVoiceDisorders,
    ...storage.participant.disorderInformation.selectedNeurologicalDisorders,
    ...storage.participant.disorderInformation.selectedPsychiatricDisorders,
    ...storage.participant.disorderInformation.selectedRespiratoryDisorders,
    ...storage.participant.disorderInformation.selectedPediatricDisorders
  ];

  const handleOnCompletion = () => {
    console.log('Confounders completed');

    onCompletion();
  };

  useEffect(() => {
    // Initial setup for questionnaire based on participant disorders
    // If the participant has any disorders, set the relevant questions to 'yes' (radio button) or 'current' (checkbox)
    const filteredGroupKeys = [
      'medicalHistoryThroat',
      'medicalHistoryLungConditions',
      'medicalHistoryNeurologicalConditions',
      'medicalHistoryPsychologicalConditions',
      'medicalHistoryCancersConditions'
    ];
    const updatedQuestionnaire = {
      ...questionnaire,
      groups: questionnaire.groups.map(group => {
        if (filteredGroupKeys.includes(group.key)) {
          return {
            ...group,
          questions: group.questions.map(q => ({
            ...q,
            value: participantDisorders.some(d => d.toLowerCase() === q.question.toLowerCase())
              ? (Array.isArray(q.value) ? ['current'] : 'yes')
              : q.value
          }))
          };
        }
        return group;
      })
    };

    setQuestionnaire(updatedQuestionnaire);
  }, []);

  useEffect(() => {
  const group = questionnaire.groups.find(g => g.key === 'physicalHealthHeightAndWeight');
  const unitQuestion = group?.questions.find(q => q.key === 'unit');
  if (!group || !unitQuestion) return;

  console.log('Unit changed:', unitQuestion.value);
    setQuestionnaire(qnr => {
      return {
        ...qnr,
        groups: qnr.groups.map(g => {
          if (g.key !== 'physicalHealthHeightAndWeight') return g;
          return {
            ...g,
            questions: g.questions.map(q => {
              if (q.key === 'height') {
                // Ensure newUnit is a string or undefined
                const newUnit = typeof unitQuestion.value === 'string' ? unitQuestion.value : undefined;
                return {
                  ...q,
                  unit: newUnit
                };
              }
              if (q.key === 'weight') {
                if (unitQuestion.value === '1') {
                  return { ...q, question: 'Weight (kg)', unit: '1' };
                } else if (unitQuestion.value === '2') {
                  return { ...q, question: 'Weight (lbs)', unit: '2' };
                }
              }
              return q;
            })
          };
        })
      };
    });
}, [questionnaire.groups.find(g => g.key === 'physicalHealthHeightAndWeight')?.questions.find(q => q.key === 'unit')?.value]);

  const handleOnChange = (questionnaire: Questionnaire) => {
    const medicalHistoryEarsNoseMouthGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryEarsNoseMouth');
    const medicalHistoryThroatGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryThroat');
    const medicalHistorySpeechGroup = questionnaire.groups.find(x => x.key === 'medicalHistorySpeech');
    const hasYesInEarsNoseMouthSelected = medicalHistoryEarsNoseMouthGroup?.questions.some(q => q.value === 'yes') || false;
    const hasPastOrCurrentThroatSelected = medicalHistoryThroatGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
    const hasYesInSpeechSelected = medicalHistorySpeechGroup?.questions.some(q => q.value === 'yes') || false;
    const medicalHistoryLungConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryLungConditions');
    const hasYesInLungConditionsSelected = medicalHistoryLungConditionsGroup?.questions.some(q => q.value === 'yes') || false;
    const medicalHistoryNeurologicalConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryNeurologicalConditions');
    const hasYesInNeurologicalConditionsSelected = medicalHistoryNeurologicalConditionsGroup?.questions.some(q => q.value === 'yes') || false;
    const medicalHistoryPsychologicalConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryPsychologicalConditions');
    const hasPastOrCurrentInPsychologicalConditionsSelected = medicalHistoryPsychologicalConditionsGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
    const medicalHistoryCancerConditionsGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryCancersConditions');
    const hasPastOrCurrentInCancerConditionsSelected = medicalHistoryCancerConditionsGroup?.questions.some(q => Array.isArray(q.value) && (q.value.includes('past') || q.value.includes('current'))) || false;
    const medicalHistoryMedicationsOrSubstancesGroup = questionnaire.groups.find(x => x.key === 'medicalHistoryMedicationsOrSubstances');
    const painMedicationQuestion = medicalHistoryMedicationsOrSubstancesGroup?.questions.find(q => q.key === 'medicationPain');
    const hormoneMedicationQuestion = medicalHistoryMedicationsOrSubstancesGroup?.questions.find(q => q.key === 'medicationHormones');

    const updatedQuestionnaire = {
      ...questionnaire,
      groups: questionnaire.groups.map(group => {
        // set surgical interventions if at least one condition is 'yes' or 'current'
        if(group.key === 'medicalHistoryEarsNoseMouthThroatSpeechSurgicalInterventions') {          // set surgical interventions if at least one condition is 'yes' or 'current'
          if (hasYesInEarsNoseMouthSelected || hasPastOrCurrentThroatSelected || hasYesInSpeechSelected) {
            return {
              ...group,
              questions: group.questions.map(q => q.type === 'multiple-choice' && !q.value ? { ...q, value: 'no' } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q => q.type === 'multiple-choice' ? { ...q, value: '' } : q
              ),
            };
          }
        }

        // set non-surgical interventions if at least one condition is 'yes' or 'current'
        if(group.key === 'medicalHistoryEarsNoseMouthThroatSpeechNonSurgicalInterventions') {
          if (hasYesInEarsNoseMouthSelected || hasPastOrCurrentThroatSelected || hasYesInSpeechSelected) {
             return {
              ...group,
              questions: group.questions.map(q =>
                (q.type === 'multiple-choice-multi-select' && (!q.value || (Array.isArray(q.value) && q.value.length === 0))) ? { ...q, value: ['never'] } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice-multi-select' ? { ...q, value: [] } : q
              ),
            };
          }
        }
        // set lung treatments if at least one lung condition is 'yes'
        if (group.key === 'medicalHistoryLungTreatments') {
          if (hasYesInLungConditionsSelected) {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.key === 'lungTreatmentOther'
                  ? q
                  : (q.type === 'multiple-choice' && !q.value ? { ...q, value: 'no' } : q)
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.key === 'lungTreatmentOther'
                  ? q
                  : (q.type === 'multiple-choice' ? { ...q, value: '' } : q)
              ),
            };
          }
        }

        // set neurological treatments if at least one neurological condition is 'yes'
        if (group.key === 'medicalHistoryNeurologicalTreatments') {
          if (hasYesInNeurologicalConditionsSelected) {
            return {
              ...group,
              questions: group.questions.map(q =>
                (q.type === 'multiple-choice-multi-select' && (!q.value || (Array.isArray(q.value) && q.value.length === 0))) ? { ...q, value: ['never'] } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice-multi-select' ? { ...q, value: [] } : q
              ),
            };
          }
        }

        // set psychological treatments if at least one psychological condition is 'past' or 'current'
        if (group.key === 'medicalHistoryPsychologicalTreatments') {
          if (hasPastOrCurrentInPsychologicalConditionsSelected) {
            return {
              ...group,
              questions: group.questions.map(q =>
                (q.type === 'multiple-choice-multi-select' && (!q.value || (Array.isArray(q.value) && q.value.length === 0))) ? { ...q, value: ['never'] } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice-multi-select' ? { ...q, value: [] } : q
              ),
            };
          }
        }

        // set cancer treatments if at least one cancer condition is 'past' or 'current'
        if (group.key === 'medicalHistoryCancersTreatments') {
          if (hasPastOrCurrentInCancerConditionsSelected) {
            return {
              ...group,
              questions: group.questions.map(q =>
                (q.type === 'multiple-choice-multi-select' && (!q.value || (Array.isArray(q.value) && q.value.length === 0))) ? { ...q, value: ['never'] } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice-multi-select' ? { ...q, value: [] } : q
              ),
            };
          }
        }

        // set sub-category values to 'no' based on pain medication question is 'yes'
        if (group.key === 'medicalHistoryPainMedications') {
          if (painMedicationQuestion?.value === 'yes') {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice' && !q.value ? { ...q, value: 'no' } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice' ? { ...q, value: '' } : q
              ),
            };
          }
        }

        // set sub-category values to 'no' based on hormone medication question is 'yes'
        if (group.key === 'medicalHistoryHormoneMedications') {
          if (hormoneMedicationQuestion?.value === 'yes') {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice' && !q.value ? { ...q, value: 'no' } : q
              ),
            };
          } else {
            return {
              ...group,
              questions: group.questions.map(q =>
                q.type === 'multiple-choice' ? { ...q, value: '' } : q
              ),
            };
          }
        }

        return group;
      }),
    };

    setQuestionnaire(updatedQuestionnaire);
  }

  console.log('Confounders component rendered', questionnaire);

  return (
    <div>
      <QuestionnaireTaskView questionnaire={questionnaire} onChange={handleOnChange} onCompletion={handleOnCompletion  } />
    </div>
  );
}
